import { TechNode } from '../../types';

/**
 * Compute domain nodes
 * Processing power and architectures
 */
export const COMPUTE_NODES: TechNode[] = [
  // === FOUNDATIONAL (Tier 0) ===
  {
    id: 'compute-integrated-circuit',
    name: 'Integrated Circuits',
    description: 'Transistors, resistors, and capacitors on a single semiconductor substrate',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 1958,
    importance: 10,
    tier: 0,
    trl: 9,
    keyPlayers: ['Jack Kilby', 'Robert Noyce', 'Texas Instruments', 'Fairchild'],
    milestones: [
      {
        date: '1958-09-12',
        title: 'First working IC',
        description: 'Kilby demonstrated phase-shift oscillator on germanium at TI',
        significance: 'breakthrough',
        source: 'https://www.nobelprize.org/prizes/physics/2000/kilby/lecture/',
      },
    ],
  },
  {
    id: 'compute-gpu',
    name: 'Graphics Processing Units',
    description: 'Massively parallel processors',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 1999,
    importance: 9,
    tier: 0,
    trl: 9,
    keyPlayers: ['NVIDIA', 'AMD', 'Intel'],
    milestones: [
      {
        date: '1999-08-31',
        title: 'GeForce 256',
        description: 'NVIDIA markets first GPU with integrated transform and lighting',
        significance: 'breakthrough',
        source: 'https://www.nvidia.com/en-us/geforce/news/geforce-256-worlds-first-gpu/',
      },
    ],
  },

  // === CURRENT FRONTIER (Tier 1) ===
  {
    id: 'compute-5nm',
    name: '5nm Process Node',
    description: 'Advanced chip manufacturing at 5nm scale',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 2020,
    importance: 8,
    tier: 1,
    trl: 9,
    keyPlayers: ['TSMC', 'Samsung'],
    milestones: [
      {
        date: '2020-09-15',
        title: 'TSMC 5nm Volume',
        description: 'TSMC 5nm in volume production (Apple A14, M1)',
        significance: 'major',
        source: 'https://www.tsmc.com/english/dedicatedFoundry/technology/logic',
      },
    ],
  },
  {
    id: 'compute-3nm',
    name: '3nm Process Node',
    description: 'Cutting-edge chip manufacturing',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 2022,
    importance: 8,
    tier: 1,
    trl: 9,
    dependencies: ['compute-5nm'],
    keyPlayers: ['TSMC', 'Samsung'],
    milestones: [
      {
        date: '2022-12-29',
        title: 'TSMC 3nm Production',
        description: 'TSMC starts 3nm volume production',
        significance: 'major',
        source: 'https://www.tsmc.com/english/dedicatedFoundry/technology/logic',
      },
    ],
  },
  {
    id: 'compute-2nm',
    name: '2nm Process Node',
    description: 'Next-generation chip manufacturing',
    domain: 'compute',
    status: 'in_progress',
    importance: 8,
    tier: 1,
    trl: 6,
    dependencies: ['compute-3nm'],
    keyPlayers: ['TSMC', 'Intel', 'Samsung'],
    progress: 70,
    yearEstimated: 2025,
    milestones: [
      {
        date: '2024-06-01',
        title: 'TSMC 2nm Risk Production',
        description: 'TSMC announces 2nm (N2) risk production schedule',
        significance: 'major',
        source: 'https://www.tsmc.com/english/dedicatedFoundry/technology/logic',
      },
    ],
  },
  {
    id: 'compute-ai-accelerators',
    name: 'AI Accelerators',
    description: 'Purpose-built chips for machine learning',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 2016,
    importance: 9,
    tier: 1,
    trl: 9,
    dependencies: ['compute-gpu'],
    keyPlayers: ['NVIDIA', 'Google TPU', 'Cerebras', 'Groq'],
    milestones: [
      {
        date: '2016-05-18',
        title: 'Google TPU',
        description: 'Google announces Tensor Processing Unit for ML inference',
        significance: 'breakthrough',
        source: 'https://cloud.google.com/blog/products/ai-machine-learning/google-cloud-offer-tpu-machine-learning',
      },
      {
        date: '2024-03-18',
        title: 'NVIDIA Blackwell',
        description: 'Next-gen AI chips with 20 petaflops FP4',
        significance: 'major',
        source: 'https://www.nvidia.com/en-us/data-center/technologies/blackwell-architecture/',
      },
    ],
  },

  // === MEMORY & INTERCONNECT (Tier 2) ===
  {
    id: 'compute-hbm',
    name: 'High Bandwidth Memory',
    description: 'Stacked memory for AI workloads',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 2015,
    importance: 8,
    tier: 2,
    trl: 8,
    keyPlayers: ['SK Hynix', 'Samsung', 'Micron'],
    milestones: [
      {
        date: '2015-11-01',
        title: 'HBM2 Standard',
        description: 'JEDEC publishes HBM2 standard; AMD Fiji first HBM GPU',
        significance: 'major',
        source: 'https://www.jedec.org/standards-documents/docs/jesd235a',
      },
    ],
  },
  {
    id: 'compute-chiplets',
    name: 'Chiplet Architecture',
    description: 'Modular chip design with interconnected tiles',
    domain: 'compute',
    status: 'achieved',
    yearAchieved: 2019,
    importance: 8,
    tier: 2,
    trl: 8,
    keyPlayers: ['AMD', 'Intel'],
    milestones: [
      {
        date: '2019-06-01',
        title: 'AMD Zen 2 Chiplets',
        description: 'AMD ships Epyc and Ryzen with chiplet architecture',
        significance: 'major',
        source: 'https://www.amd.com/en/technologies/chiplet-technology',
      },
    ],
  },
  {
    id: 'compute-optical-interconnect',
    name: 'Optical Interconnects',
    description: 'Light-based chip communication',
    domain: 'compute',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 5,
    keyPlayers: ['Lightmatter', 'Ayar Labs', 'Intel'],
    progress: 45,
    yearEstimated: 2027,
    milestones: [
      {
        date: '2023-01-01',
        title: 'Ayar Labs TeraPHY',
        description: 'Optical I/O chiplets for data center and HPC',
        significance: 'major',
        source: 'https://www.ayarlabs.com/',
      },
    ],
  },

  // === NEUROMORPHIC (Tier 2-3) ===
  {
    id: 'compute-neuromorphic',
    name: 'Neuromorphic Computing',
    description: 'Brain-inspired chip architectures',
    domain: 'compute',
    status: 'in_progress',
    importance: 8,
    tier: 2,
    trl: 5,
    keyPlayers: ['Intel Loihi', 'IBM TrueNorth', 'SynSense'],
    progress: 40,
    milestones: [
      {
        date: '2017-09-01',
        title: 'Intel Loihi',
        description: 'Intel announces neuromorphic research chip (Loihi)',
        significance: 'major',
        source: 'https://www.intel.com/content/www/us/en/research/neuromorphic-computing.html',
      },
    ],
  },
  {
    id: 'compute-in-memory',
    name: 'In-Memory Computing',
    description: 'Processing data where it lives',
    domain: 'compute',
    status: 'in_progress',
    importance: 7,
    tier: 2,
    trl: 5,
    progress: 35,
    milestones: [
      {
        date: '2020-01-01',
        title: 'Memristor Crossbars',
        description: 'In-memory compute demonstrations with resistive RAM (Nature Electronics)',
        significance: 'major',
        source: 'https://www.nature.com/articles/s41928-019-0362-7',
      },
    ],
  },

  // === POST-MOORE (Tier 3+) ===
  {
    id: 'compute-1nm',
    name: 'Sub-2nm Nodes',
    description: 'Approaching atomic limits of silicon',
    domain: 'compute',
    status: 'in_progress',
    importance: 8,
    tier: 3,
    trl: 5,
    dependencies: ['compute-2nm'],
    keyPlayers: ['TSMC', 'Intel', 'ASML'],
    progress: 30,
    yearEstimated: 2027,
    milestones: [
      {
        date: '2025-01-01',
        title: 'Intel 14A / 1.4nm',
        description: 'Intel and foundries target sub-2nm nodes',
        significance: 'major',
        source: 'https://www.intel.com/content/www/us/en/newsroom/news/process-technology.html',
      },
    ],
  },
  {
    id: 'compute-carbon-nanotube',
    name: 'Carbon Nanotube Processors',
    description: 'Transistors from carbon nanotubes',
    domain: 'compute',
    status: 'in_progress',
    importance: 8,
    tier: 3,
    trl: 4,
    progress: 25,
    keyPlayers: ['MIT', 'Stanford'],
    milestones: [
      {
        date: '2019-08-28',
        title: 'First CNT Processor',
        description: 'MIT: 16-bit RISC-V processor from carbon nanotubes (Nature)',
        significance: 'breakthrough',
        source: 'https://www.nature.com/articles/s41586-019-1493-8',
      },
    ],
  },
  {
    id: 'compute-photonic',
    name: 'Photonic Computing',
    description: 'Computation using light instead of electrons',
    domain: 'compute',
    status: 'in_progress',
    importance: 8,
    tier: 3,
    trl: 4,
    dependencies: ['compute-optical-interconnect'],
    keyPlayers: ['Lightmatter', 'Luminous Computing'],
    progress: 30,
    yearEstimated: 2030,
    milestones: [
      {
        date: '2021-01-01',
        title: 'Lightmatter Envise',
        description: 'Photonic inference accelerator for datacenters',
        significance: 'major',
        source: 'https://lightmatter.co/',
      },
    ],
  },

  // === THEORETICAL (Tier 4+) ===
  {
    id: 'compute-reversible',
    name: 'Reversible Computing',
    description: 'Near-zero energy computation',
    domain: 'compute',
    status: 'theoretical',
    importance: 9,
    tier: 4,
    trl: 2,
    milestones: [
      {
        date: '1973-01-01',
        title: 'Landauer Principle',
        description: 'Landauer: minimum energy to erase a bit (foundation for reversible computing)',
        significance: 'major',
        source: 'https://link.springer.com/article/10.1007/BF02342426',
      },
    ],
  },
  {
    id: 'compute-molecular',
    name: 'Molecular Computing',
    description: 'Computing with individual molecules',
    domain: 'compute',
    status: 'theoretical',
    importance: 9,
    tier: 4,
    trl: 2,
    dependencies: ['compute-carbon-nanotube'],
    milestones: [
      {
        date: '2016-03-01',
        title: 'Molecular Logic',
        description: 'Single-molecule logic gates and circuits (Nature Nanotech.)',
        significance: 'major',
        source: 'https://www.nature.com/articles/nnano.2016.19',
      },
    ],
  },
];

export default COMPUTE_NODES;
